import React, { useCallback } from 'react';
import { ScrollText, Wrench, BookOpen } from 'lucide-react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useLayoutStore } from '../../../../store/layoutStore';
import { ShimmerText, MorphingFluid } from '../ChatArea/Indicators';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

interface AgentActivityPillProps {
  content: ToolUseBlock;
  isStreaming?: boolean;
  /** Override outer margin class. Defaults to 'my-0.5'. Pass 'my-0' inside ToolUseGroup. */
  outerClassName?: string;
}

const loadToolsFallbacksActive = (t: TFunction<'pleri/chat'>) => [
  t('gettingTheRightToolsReady'),
  t('grabbingWhatINeed'),
  t('oneMomentEquippingMyself'),
  t('pullingTheRightToolsForThis'),
];

const loadToolsFallbacksDone = (t: TFunction<'pleri/chat'>) => [
  t('readyToGo'),
  t('allSet'),
  t('toolsInHand'),
  t('goodToGo'),
];

function pickFallback(arr: string[], seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return arr[hash % arr.length];
}

function getLabel(
  t: TFunction<'pleri/chat'>,
  content: ToolUseBlock,
  active: boolean,
): string {
  const intention = content.input?.intention as string | undefined;

  if (content.name === 'loadTools') {
    if (intention) return intention;
    return active
      ? pickFallback(loadToolsFallbacksActive(t), content.id)
      : pickFallback(loadToolsFallbacksDone(t), content.id);
  }

  if (content.name === 'loadSkill') {
    if (intention) return intention;
    const names =
      (content.input?.names as string[] | undefined) ??
      (content.input?.name ? [content.input.name as string] : []);
    if (active) {
      if (!names.length) return t('loadingSkill');
      return names.length > 1
        ? t('loadingSkills', { count: names.length })
        : t('loadingName', { name: names[0] });
    }
    if (!names.length) return t('skillApplied');
    return names.length === 1
      ? t('appliedName', { name: names[0] })
      : t('appliedSkills', { count: names.length });
  }

  if (content.name === 'getSkillResource') {
    const skillName = content.input?.skillName as string | undefined;
    const filePaths = (content.input?.filePaths as string[] | undefined) ?? [];
    const skill = skillName ?? t('skillFallback');
    if (active) {
      return filePaths.length > 1
        ? t('readingFilesFromSkill', { count: filePaths.length, skill })
        : t('readingResourceFromSkill', {
            resource: filePaths[0] ?? t('resourceFallback'),
            skill,
          });
    }
    return filePaths.length > 1
      ? t('loadedFiles', { count: filePaths.length })
      : t('loadedResource', {
          resource: filePaths[0] ?? t('resourceFallback'),
        });
  }

  return '';
}

/**
 * Pill for background meta-tools: loadTools, loadSkill, getSkillResource.
 *
 * Active   → pending shimmer row (same as ToolUseContent pending)
 * Done     → same structure/interaction as ToolUseContent — clicking opens
 *            the tool detail panel so users can inspect inputs/outputs.
 */
export const AgentActivityPill: React.FC<AgentActivityPillProps> = ({
  content,
  isStreaming = false,
  outerClassName = 'my-0.5',
}) => {
  const { t } = useTranslation('pleri/chat');
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const setToolPanelOpen = useChatStore((state) => state.setToolPanelOpen);
  const setSelectedToolId = useChatStore((state) => state.setSelectedToolId);
  const selectedToolId = useChatStore((state) => state.selectedToolId);
  const isOpen = useChatStore((state) => {
    const conv = state.activeConversationId
      ? state.conversations[state.activeConversationId]
      : null;
    return conv?.toolPanelOpen || false;
  });
  const isPending = useChatStore((state) => {
    if (!activeConversationId) return false;
    const conv = state.conversations[activeConversationId];
    if (!conv?.isLoading) return false;
    return (conv.pendingToolUses ?? new Set<string>()).has(content.id);
  });
  const isMiniMode = useLayoutStore((state) => state.isMiniMode);
  const setMiniMode = useLayoutStore((state) => state.setMiniMode);

  const active = isStreaming || isPending;
  const isSelected = content.id === selectedToolId && isOpen;

  const handleClick = useCallback(() => {
    if (active || !activeConversationId) return;
    if (isMiniMode) setMiniMode(false);
    if (isSelected) {
      setToolPanelOpen(activeConversationId, false);
      setSelectedToolId(null);
    } else {
      setToolPanelOpen(activeConversationId, true);
      setSelectedToolId(content.id);
    }
  }, [
    active,
    activeConversationId,
    isMiniMode,
    isSelected,
    setMiniMode,
    setToolPanelOpen,
    setSelectedToolId,
    content.id,
  ]);

  const label = getLabel(t, content, active);
  const isLoadTools = content.name === 'loadTools';
  const isLoadSkill = content.name === 'loadSkill';

  if (!label) return null;

  // ── Active / pending ─────────────────────────────────────────────────────
  // Identical to ToolUseContent pending — not clickable while running.
  if (active) {
    return (
      <div
        className={`${outerClassName} flex items-center gap-2.5 rounded-lg bg-orange-50/60 px-2.5 py-1.5 select-none dark:bg-gray-700/10`}
      >
        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
          <MorphingFluid size="h-3.5 w-3.5" />
        </div>
        <ShimmerText text={label} />
      </div>
    );
  }

  // ── Done ─────────────────────────────────────────────────────────────────
  // Same structure, hover behaviour, and panel interaction as ToolUseContent.
  return (
    <div
      data-tool-id={content.id}
      onClick={handleClick}
      className={`group/pill ${outerClassName} flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 transition-all duration-150 select-none ${
        isSelected
          ? 'bg-orange-50/40 dark:bg-orange-950/20'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
      }`}
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-md border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-800">
          {isLoadSkill ? (
            <ScrollText
              className="h-3.5 w-3.5 text-[#FF732E]"
              strokeWidth={2}
            />
          ) : isLoadTools ? (
            <Wrench className="h-3.5 w-3.5 text-[#FF732E]" strokeWidth={2} />
          ) : (
            <BookOpen className="h-3.5 w-3.5 text-[#FF732E]" strokeWidth={2} />
          )}
        </div>
      </div>

      {/* Label */}
      <span className="min-w-0 flex-1 truncate text-sm font-normal tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </span>

      {/* Arrow — hover-only, same as ToolUseContent */}
      <svg
        className={`h-3.5 w-3.5 flex-shrink-0 text-transparent transition-all duration-150 ${
          isSelected
            ? 'group-hover/pill:text-[#FF732E]'
            : 'group-hover/pill:text-gray-400 dark:group-hover/pill:text-gray-500'
        }`}
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="2.5"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
        />
      </svg>
    </div>
  );
};
